import {Delete} from '@mui/icons-material';
import {useCanAny, useCan} from 'src/permissions/hooks';
import {IconButton} from '@mui/material';
import {FC, useContext} from 'react';
import {useParams} from 'react-router-dom';
import {companyApi, locationsApi} from 'src/api';
import {useAppDispatch} from 'src/app/hooks';
import LocationInfoContext from 'src/pages/LocationInfo/LocationInfo.context';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {AppDispatch} from 'src/types';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';
import {RemoveUserFormLocationButtonType} from './RemoveUserFormLocationButton.types';

type DeleteCompanyFromLocationMutationTrigger = ReturnType<
    typeof companyApi.useRemoveLocationFromCompanyMutation
>[0];
type DeleteUserFromLocationMutationTrigger = ReturnType<
    typeof locationsApi.useDeleteUserFromLocationMutation
>[0];

const handleDelete =
    (
        trigger: DeleteUserFromLocationMutationTrigger,
        companyTrigger: DeleteCompanyFromLocationMutationTrigger,
        dispatch: AppDispatch,
        locationId: number,
        userId: number,
        context: any,
        isCompanyUser: boolean,
        refetch: () => void
    ) =>
        async () => {
            const data = {
                businessId: context.businessId,
                locationId,
                userId,
            };
            dispatch(preloaderChangeStatus(true));

            try {
                if (isCompanyUser) {
                    await companyTrigger({
                        companyId: userId,
                        data: {
                            businessId: context.businessId,
                            locationId,
                        },
                    }).unwrap();
                } else {
                    await trigger(data).unwrap();
                }
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                refetch();
                dispatch(preloaderChangeStatus(false));
            }
        };

export const RemoveUserFormLocationButton: FC<RemoveUserFormLocationButtonType> = ({
                                                                                       userId,
                                                                                       isCompanyUser,
                                                                                   }) => {
    // Detaching a client from a location, by whichever of the two names the caller holds it under.
    const canRemove = useCanAny('locations.assignUser', 'companies.assignLocations');
    const {id: locationId} = useParams();

    const dispatch = useAppDispatch();

    const context = useContext(LocationInfoContext);

    const [trigger] = locationsApi.useDeleteUserFromLocationMutation();
    const [companyTrigger] = companyApi.useRemoveLocationFromCompanyMutation();
    const {refetch} = locationsApi.useGetLocationByIdQuery({
        locationId: +(locationId ?? 0),
        businessId: (context as any)?.businessId ?? 0,
    });

    if (!canRemove) {
        return null;
    }

    return (
        <>
            {locationId && (
                <IconButton
                    onClick={handleDelete(
                        trigger,
                        companyTrigger,
                        dispatch,
                        +locationId,
                        userId,
                        context,
                        isCompanyUser,
                        refetch
                    )}
                >
                    <Delete/>
                </IconButton>
            )}
        </>
    );
};
